import { reject } from "../../TypingUtils";
import { IEquatable } from "../IEquatable";
import { IterUtils } from "../../IterableUtils";

export class Map2<K extends IEquatable<K>, V> {
    private readonly hashMap = new Map<number, Array<[K, V]>>();

    public constructor(iterable?: Iterable<readonly [K, V]>) {
        if (iterable != undefined) {
            for (const [key, value] of iterable) {
                this.set(key, value);
            }
        }
    }

    public set(key: K, value: V): void {
        this.setOrUpdate(
            key,
            () => value,
            () => value
        );
    }

    public getOrSet(key: K, defaultValueFactory: () => V): V {
        return this.setOrUpdate(key, defaultValueFactory, (_, v) => v);
    }

    public get(key: K): V | undefined {
        return this.getEntryOrUndefined(key)?.[1];
    }

    public has(key: K): boolean {
        return this.getEntryOrUndefined(key) != undefined;
    }

    public remove(key: K): boolean {
        const hash = this.getKeyHash(key);
        const list = this.hashMap.get(hash);
        if (list != undefined) {
            const removingEntry = Iterator.from(IterUtils.iterateEntries(list)).find(([_, [itemKey]]) =>
                this.areKeyEqual(itemKey, key)
            );
            if (removingEntry != undefined) {
                list.splice(removingEntry[0], 1);
                return true;
            }
        }
        return false;
    }

    public get isEmpty(): boolean {
        return this.hashMap.size === 0;
    }

    /**
     * Обновляет значение в словаре по ключю, если значения нет -- устнавит новое
     * @param key ключ словаря для робновления
     * @param newValueFactory Если значения нет -- будет установлен результат этой функции
     * @param updateValueFactory Если значение есть -- будет установлен результат этой функции
     */
    public setOrUpdate(key: K, newValueFactory: (key: K) => V, updateValueFactory: (key: K, update: V) => V): V {
        const hash = this.getKeyHash(key);
        const list = this.hashMap.get(hash);
        if (list == undefined) {
            const result = newValueFactory(key);
            this.hashMap.set(hash, [[key, result]]);
            return result;
        } else {
            const itemIndex = list.findIndex(x => this.areKeyEqual(x[0], key));
            if (itemIndex == -1) {
                const result = newValueFactory(key);
                list.push([key, result]);
                return result;
            } else {
                const entry = list[itemIndex] ?? reject();
                entry[1] = updateValueFactory(key, entry[1]);
                return entry[1];
            }
        }
    }

    public keys(): Iterable<K> {
        return Iterator.from(this.entries()).map(e => e[0]);
    }

    public values(): Iterable<V> {
        return Iterator.from(this.entries()).map(e => e[1]);
    }

    public entries(): Iterable<[key: K, value: V]> {
        return Iterator.from(this.hashMap.values()).flatMap(x => x);
    }

    public clear(): void {
        this.hashMap.clear();
    }

    protected getKeyHash(key: K): number {
        return key.getHash();
    }

    protected areKeyEqual(left: K, right: K): boolean {
        return left.isEquals(right);
    }

    private getEntryOrUndefined(key: K): [K, V] | undefined {
        const hash = this.getKeyHash(key);
        return this.hashMap.get(hash)?.find(x => this.areKeyEqual(x[0], key));
    }
}
